import { useMemo, type CSSProperties } from 'react';
import { useAuth } from '@helios/auth-firebase-web';
import type { QuerySpec } from '@helios/powersync-client-core';
import { qUserProviderLink, qNoOp, type UserProviderLinkRow } from '@helios/data-access';
import { usePowerSyncQuery } from '../adapters/powersync';
import { isProviderScopedRoles } from './appPolicy';

/**
 * PRD.293 — Doctor provider-scoped views (Tier 1).
 *
 * Resolves whether the signed-in user should have their Tier 1 surfaces (queue,
 * dashboard KPIs, appointments) scoped to a single provider, and — when so —
 * which provider. Only "pure doctors" are scoped (see {@link isProviderScopedRoles});
 * operational users are never scoped and never trigger a provider-link lookup.
 *
 * This is a UI/query-scoping helper, NOT a security boundary.
 */
export interface ProviderScope {
  /** True when the signed-in user is a pure doctor and should see only their own workload. */
  scoped: boolean;
  /** The linked provider id, or null when scoped but not yet resolved / not linked. */
  providerId: string | null;
  /** False while the provider-link query is still loading (scoped users only). */
  ready: boolean;
}

export function useProviderScope(): ProviderScope {
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const uid = user?.uid ?? '';
  const roles = orgId ? (claims?.orgs?.[orgId]?.roles ?? []) : [];
  const scoped = isProviderScopedRoles(roles);

  // Resolve the provider link ONLY for scoped users. Operational users pass
  // qNoOp, so no query against user_provider_links runs and their Tier 1
  // behavior is unchanged (backwards-compatible).
  const { data: linkRows = [], loaded } = usePowerSyncQuery<UserProviderLinkRow>(
    scoped && orgId && uid ? qUserProviderLink(orgId, uid) : qNoOp('provider-scope'),
  );

  return useMemo<ProviderScope>(() => {
    if (!scoped) return { scoped: false, providerId: null, ready: true };
    return { scoped: true, providerId: linkRows[0]?.provider_id ?? null, ready: loaded };
  }, [scoped, linkRows, loaded]);
}

/**
 * How a Tier 1 query should run under provider scope:
 * - `all`      → clinic-wide (operational users) — identical to pre-PRD.293 behavior
 * - `provider` → scoped to `providerId`
 * - `blocked`  → doctor with no link, or link still loading → fail closed (return no rows)
 */
export type ScopeResolution =
  | { mode: 'all' }
  | { mode: 'provider'; providerId: string }
  | { mode: 'blocked' };

export function resolveScope(scope: ProviderScope): ScopeResolution {
  if (!scope.scoped) return { mode: 'all' };
  if (!scope.ready || !scope.providerId) return { mode: 'blocked' };
  return { mode: 'provider', providerId: scope.providerId };
}

/**
 * Build the query spec for a scoped Tier 1 surface. `build(providerId?)` receives
 * the provider id when scoped, or undefined to run clinic-wide. Fail-closed
 * (blocked) callers get {@link qNoOp} so the surface shows no rows.
 */
export function scopedSpec(
  scope: ProviderScope,
  tag: string,
  build: (providerId?: string) => QuerySpec,
): QuerySpec {
  const res = resolveScope(scope);
  if (res.mode === 'blocked') return qNoOp(tag);
  if (res.mode === 'provider') return build(res.providerId);
  return build();
}

/**
 * True when a scoped doctor has finished resolving but has NO provider link —
 * the state that should surface the "ask an admin to link you" notice.
 */
export function isProviderLinkMissing(scope: ProviderScope): boolean {
  return scope.scoped && scope.ready && !scope.providerId;
}

// ─── Fail-closed notice ───

const noticeStyle: CSSProperties = {
  margin: 'var(--sp-4, 16px) 0',
  padding: 'var(--sp-4, 16px) var(--sp-5, 20px)',
  background: '#fffbeb',
  border: '1px solid #fde68a',
  borderRadius: 'var(--radius-lg, 14px)',
  color: '#92400e',
  fontSize: 'var(--font-size-sm, 14px)',
  lineHeight: 'var(--line-height-base, 1.5)',
};

/**
 * PRD.293 — shown to a scoped doctor whose account isn't linked to a provider.
 * Fail-closed: rather than falling back to clinic-wide data, we surface the
 * misconfiguration so an admin can link the account.
 */
export function ProviderLinkMissingNotice() {
  return (
    <div style={noticeStyle} role="status">
      <strong>Your account isn't linked to a provider yet.</strong> Ask an administrator to link
      your account so your queue, schedule, and dashboard can show your patients.
    </div>
  );
}
